/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import React from "react";
import EntryAndExitFormDetailsSectionInfo
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/EntryAndExitFormDetailsSectionInfo";
import {permissionObj} from "../../../../../../../Helpers/permission";
import routes from "../../../../../../../Routes/routes";
import DivContainerBody from "../../../../../../../components/DivContainerBody";
import HeadTittle from "../../../../../../../components/HeadTittle";
import CustomChip from "../../../../../../../components/CustomChip";
import {colors} from "../../../../../../../enums/colors";
import {EditIcon, IconDocumentWarning, IconVehicleInMotion} from "../../../../../../../components/buttons/IconButtons";
import ButtonAction from "../../../../../../../components/buttons/ButtonAction";
import ReportDispatchModal from "../Modal/ReportDispatchModal";
import StatusBadgeSection
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/Sections/StatusBadgeSection";
import DiscrepancySection
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/Sections/DiscrepancySection";
import GuardGateLogSection from "../Sections/GuardGateLogSection";
import {
    getGateLogNotePlaceholder,
    MATERIAL_CONTROL_REGISTERED_GATE_STATUS,
    showsGuardGateMovementLog,
    showsGuardTransport,
    showsGuardTransportRecord,
    wasGuardReportedOnVehicleMovement
} from "../Model/Model";
import {wasMaterialControlDispatched} from "../../../../../MaterialControl/Model/Model";
import GateLogCard
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/Details/Sections/GateLogCard";
import CratesAndPalletsTransportSection, {getGuardTransportFields}
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/CratesAndPalletsReturn/CratesAndPalletsTransportSection";
import ExpectedQuantitiesSection, {getGuardQuantityFields}
    from "../../../../../MaterialControl/SubComponents/EntryAndExitForms/CratesAndPalletsReturn/ExpectedQuantitiesSection";
import {STATUS_MODEL} from "../../../../../../../enums/statusEnum";

/**
 * Detail of a crates and pallets return read by the guard at the gate.
 *
 * The information of the folio is the one the requester registered, so the
 * guard only reads it and completes what was left blank: while the vehicle is
 * awaited, the notes of the arrival and the evidence that supports it; once it
 * arrives, the transport that returns the crates and the quantities expected
 * at the gate. A movement reported with discrepancies displays them above the
 * log, because it has to be reviewed before the crates can be dispatched.
 *
 * The exit of the trip is the movement the gate writes on its own log, so the
 * detail the guard reads does not repeat it and only the requester follows the
 * folio with it.
 *
 * @param {Object} props - Information of the folio and handlers of the gate log.
 * @param {Function} props.t - Translation handler.
 * @param {Function} props.navigate - Navigation handler.
 * @param {boolean} props.DEV - Whether the development mode is active.
 * @param {Array} props.permission - Permissions of the user reading the folio.
 * @param {Object} props.contentCardRef - Reference of the content of the information card.
 * @param {Object} props.contentTableRef - Reference of the content of the gate log.
 * @param {Object} props.containerCardRef - Reference of the container of the information card.
 * @param {Object} props.containerTableRef - Reference of the container of the gate log.
 * @param {Object} props.item - Folio the movement belongs to.
 * @param {Array} props.evidence - Evidence registered by the requester.
 * @param {Object} props.form - Type of the form being displayed.
 * @param {boolean} props.showBadge - Whether the dispatch of the folio is displayed.
 * @param {Object} props.gateLog - Gate log captured by the guard.
 * @param {Function} props.handleChange - Change handler of the fields of the gate log.
 * @param {Object} props.photoPicker - Evidence of the movement captured at the gate.
 * @param {Function} props.setPhotoPicker - State updater of the evidence of the gate.
 * @param {boolean} props.emptyFields - Whether the required fields are marked.
 * @param {boolean} props.showConfirmModal - Whether the confirmation of the report is displayed.
 * @param {Function} props.handleDispatch - Registers the dispatch of the movement.
 * @param {Function} props.openReportModal - Opens the report of the discrepancies.
 * @param {Function} props.handleActionModal - Registers the confirmed report with its comment and evidence.
 * @param {Function} props.closeModal - Closes the confirmation of the report.
 * @param {Function} props.goBack - Returns to the list of forms.
 *
 * @returns {JSX.Element} Detail of the folio with the gate log of the guard.
 */
const GuardCratesAndPalletsReturnDetails = ({
                                          t,
                                          navigate,
                                          DEV,
                                          isGuard,
                                          permission,
                                          contentCardRef,
                                          contentTableRef,
                                          containerCardRef,
                                          containerTableRef,
                                          item,
                                          evidence,
                                          form,
                                          showBadge,
                                          gateLog,
                                          handleChange,
                                          photoPicker,
                                          setPhotoPicker,
                                          emptyFields,
                                          showConfirmModal,
                                          handleDispatch,
                                          openReportModal,
                                          handleActionModal,
                                          closeModal,
                                          goBack
                                      }) => {

    if (!DEV && !isGuard &&
        !permission?.includes(permissionObj.WEB_ENTRY_AND_EXIT_FORMS_READ)
    )
        navigate(routes.forbidden);

    // The dispatch hands the material over and the departure takes it out of
    // the plant, a folio reported with discrepancies is still open, so the
    // actions that close it are read until one of those movements is
    // registered.
    const isClosed = MATERIAL_CONTROL_REGISTERED_GATE_STATUS.includes(item?.dispatchStatusId);

    const hasDiscrepancies = item?.dispatchStatusId === STATUS_MODEL.DISCREPANCY;

    // The report replaces the status the movement was registered with, so a
    // folio reported with discrepancies keeps reading the log of the gate, only
    // read, whether the guard already wrote it or was still waiting to.
    const showReportedLog = hasDiscrepancies;

    // The gate writes nothing else on a folio that was reported, so its log is
    // read and no longer completed.
    const isLogDisabled = isClosed || hasDiscrepancies || wasMaterialControlDispatched(item);

    // A folio reported with discrepancies has to be reviewed before the gate
    // writes anything else on it, so it is only read until the report is attended
    const canClose = (DEV || isGuard)
        && !isClosed
        && !hasDiscrepancies;

    // The gate writes the log of the movement the folio declared — the arrival
    // of the vehicle or its departure — and keeps reading it once that movement
    // was registered, so both are read the same way every form reads them.
    // The report replaces the status the folio was waiting in, so a folio
    // reported during a movement of the vehicle keeps reading that same log,
    // only read.
    const reportedOnVehicleMovement = wasGuardReportedOnVehicleMovement(item);
    const showsGateMovementLog = showsGuardGateMovementLog(item) || reportedOnVehicleMovement;

    // Once the arrival is registered the folio waits for its dispatch, so what
    // the requester left blank is the block the guard completes before closing
    // the movement.
    const isPendingDispatch = item?.dispatchStatusId === STATUS_MODEL.PENDING_TO_DISPATCH;

    // The values the gate captures for the movement are read once it
    // registered them, and a folio reported while the guard was completing
    // them keeps reading the same block, only read, because the report says
    // what was found on it and does not take it out of the movement.
    //
    // A folio reported during a movement of the vehicle never reached that
    // block or already closed it, so the report is read with the log of that
    // movement and the values of the dispatch are not opened on it.
    const isDispatched = wasMaterialControlDispatched(item);
    const reportedOnDispatch = hasDiscrepancies && !reportedOnVehicleMovement;
    // The gate writes one log at a time, so the block the dispatch completes is
    // not opened while the guard is registering a movement of the vehicle: what
    // closed the folio is already read in the record of the folio.
    const showMovementValues = !showsGateMovementLog
        && (isPendingDispatch || isDispatched || reportedOnDispatch);

    // The transport and the quantities are optional for the requester, so the
    // folio is the one that says which of them belong to the gate: what the
    // requester registered is only read and what was left blank is the block
    // the guard completes before the movement is closed
    // The log is written for the movement the folio waits for, so the notes ask
    // for what is seen when the vehicle comes in or for what is seen when it
    // leaves the plant
    const notePlaceholder = getGateLogNotePlaceholder({
        statusId: item?.dispatchStatusId,
        movementDirection: item?.movementDirection
    });

    // The vehicle is read in the moment it is in front of the guard, so the
    // transport is captured while the folio waits for the arrival of the unit,
    // or with the dispatch when the folio goes straight to it
    const showTransport = showsGuardTransport(item);

    // The vehicle that was already written is read again on the step that
    // follows the one it was captured in, so the guard reads the unit the
    // movement travels with and no longer writes it
    const showTransportRecord = showsGuardTransportRecord(item);

    // The transport is only written in the moment the vehicle is at the gate, so
    // the step that reads it back reads it whole
    const isTransportDisabled = isLogDisabled || !showTransport;

    const transportFields = getGuardTransportFields(item, isTransportDisabled);
    const quantityFields = getGuardQuantityFields(item);

    const transportSection = (showTransport || showTransportRecord) && (
        <CratesAndPalletsTransportSection
            t={t}
            materialRequest={gateLog}
            fields={transportFields}
            handleChange={handleChange}
            emptyFields={emptyFields}
            disabled={isTransportDisabled}
            contentClassName={'row input-padding-top pb-3'}
        />
    );

    return (
        <>
            <HeadTittle
                name={'details'}
                onBack={goBack}
                divStack={
                    <CustomChip legend={t(form.trans)}
                                background={colors.secondary100}
                                fontColor={colors.secondary700}
                                borderColor={colors.secondary150}
                                variant={'compact'}
                                icon={form.Icon}
                                classNameIcon={'icon-color-secondary-700 h-16px w-16px'}
                    />
                }
            >
                {canClose &&
                    <>
                        <ButtonAction
                            id='btn_gcaprd_dispatch'
                            className='btn-icon-edit'
                            Icon={EditIcon}
                            name='dispatch'
                            onClick={() => handleDispatch()}
                        />
                        <ButtonAction
                            id='btn_gcaprd_report'
                            className='btn-icon-delete'
                            Icon={IconDocumentWarning}
                            name='report'
                            onClick={() => openReportModal()}
                        />
                    </>
                }
            </HeadTittle>
            <DivContainerBody>
                <div className={"mb-4"}>
                    <StatusBadgeSection
                        t={t}
                        item={item}
                        showBadge={showBadge}
                    />
                </div>
                <div className='row px-0'>
                    <div className='col-sm-12 col-md-4 col-lg-4 mb-lg-0 mb-md-3 mb-sm-3 pl-0 pr-sm-0 pr-md-3'
                         ref={containerCardRef}>
                        <EntryAndExitFormDetailsSectionInfo
                            item={item}
                            evidence={evidence}
                            form={form}
                            contentCardRef={contentCardRef}
                            showDepartureRecord={false}
                            showTripRecord={false}
                        />
                    </div>
                    <div className='col-sm-12 col-md-8 col-lg-8 px-0' ref={containerTableRef}>
                        {/* The detail stacks the sections the guard closes the movement with, so
                            the column is the one that scrolls and every card is displayed whole. */}
                        <div className={"overflow-y-auto"} ref={contentTableRef}>
                            {hasDiscrepancies &&
                                <div className={"mb-3"}>
                                    <DiscrepancySection
                                        t={t}
                                        reviewedBy={item?.dispatchUser}
                                        reviewedAt={item?.dispatchDate}
                                        reason={item?.dispatchReason}
                                        files={item?.discrepancyFiles}
                                    />
                                </div>
                            }

                            {showMovementValues &&
                                <div className={"mb-3"}>
                                    <GateLogCard
                                        t={t}
                                        title={'gate_log'}
                                    >
                                        <div className={"px-3"}>
                                            {transportSection}

                                            <ExpectedQuantitiesSection
                                                t={t}
                                                materialRequest={gateLog}
                                                fields={quantityFields}
                                                handleChange={handleChange}
                                                emptyFields={emptyFields}
                                                disabled={isLogDisabled}
                                            />
                                        </div>
                                    </GateLogCard>
                                </div>
                            }

                            {/* The crates come back into the plant, so the notes are written
                                for what is seen when the vehicle arrives, even though the folio
                                travels with the exit every return is registered with. */}
                            {(showsGateMovementLog || isPendingDispatch || isClosed || showReportedLog) &&
                                <GuardGateLogSection
                                    t={t}
                                    {...(showsGateMovementLog
                                        ? {
                                            title: 'gate_log',
                                            // The movement of the vehicle is
                                            // registered after the dispatch, so
                                            // its log is written until the gate
                                            // closes the folio with it
                                            disabled: isClosed || hasDiscrepancies
                                        }
                                        : {
                                            title: 'dispatch_details',
                                            evidenceCard: false,
                                            icon: IconVehicleInMotion,
                                            noteTrans: 'dispatch_note',
                                            disabled: isLogDisabled
                                        })}
                                    gateLog={gateLog}
                                    handleChange={handleChange}
                                    photoPicker={photoPicker}
                                    setPhotoPicker={setPhotoPicker}
                                    emptyFields={emptyFields}
                                    notePlaceholder={notePlaceholder}
                                    // The log of a movement of the vehicle opens
                                    // with the unit it was read with, so the
                                    // transport is its first section
                                    transportSection={showMovementValues ? null : transportSection}
                                />
                            }
                        </div>
                    </div>
                </div>
            </DivContainerBody>

            { showConfirmModal &&
                <ReportDispatchModal
                    show={showConfirmModal}
                    onHide={closeModal}
                    onConfirm={(comment, files) => handleActionModal(comment, files)}
                    title_modal={'report_dispatch'}
                    description={t('are_you_sure_to_proceed')}
                    subDescription={t('please_provide_the_required_information_below')}
                />
            }
        </>
    );
};

export default GuardCratesAndPalletsReturnDetails;
